/**
 * Profile Photo Caching Utility
 * Implements localStorage caching with timestamp-based validation
 * for optimal performance and minimal API calls
 */

import { AUTH_ENDPOINTS } from "../config/apiConfig";

const CACHE_KEY = "user_profile_photo";
const CACHE_TIMESTAMP = "photo_timestamp";

/**
 * Get profile photo with smart caching - validates against user timestamp first
 * @param {string} token - Authentication token
 * @param {string} currentTimestamp - Current photoUpdatedAt from UserContext (optional)
 * @returns {Promise<string|null>} Profile photo URL or null
 */
export const getProfilePhoto = async (token, currentTimestamp = null) => {
  try {
    // If no token, return null immediately
    if (!token) {
      return null;
    }

    const cachedPhoto = localStorage.getItem(CACHE_KEY);
    const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP);

    // OPTIMIZATION: If we have a timestamp from UserContext, validate without API call!
    if (
      currentTimestamp &&
      cachedPhoto &&
      cachedTimestamp === currentTimestamp
    ) {
      // Cache is valid - return immediately without API call! ⚡
      return cachedPhoto;
    }

    // Cache invalid or no timestamp provided - fetch from API
    const response = await fetch(AUTH_ENDPOINTS.PROFILE, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch user data");
    }

    const data = await response.json();
    const user = data.user || data;

    // Check if cached version is still valid
    if (cachedPhoto && cachedTimestamp === user.photoUpdatedAt) {
      // Cached version is good - use it
      return cachedPhoto;
    }

    // Cache is outdated or missing - save new photo
    if (user.profilePhotoUrl) {
      localStorage.setItem(CACHE_KEY, user.profilePhotoUrl);
      localStorage.setItem(CACHE_TIMESTAMP, user.photoUpdatedAt || "");
    } else {
      // User has no photo - clear cache
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP);
    }

    return user.profilePhotoUrl || null;
  } catch (error) {
// Return cached version on error (offline support)
    return localStorage.getItem(CACHE_KEY) || null;
  }
};

/**
 * Validate cache synchronously against a timestamp
 * @param {string} currentTimestamp - Current photoUpdatedAt timestamp
 * @returns {boolean} True if cache is valid
 */
export const validateCacheSync = (currentTimestamp) => {
  if (!currentTimestamp) return false;
  const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP);
  return cachedTimestamp === currentTimestamp;
};

/**
 * Get cached photo synchronously (instant load)
 * Use this for immediate rendering, then update with async fetch if needed
 * @returns {string|null} Cached profile photo URL or null
 */
export const getCachedPhoto = () => {
  return localStorage.getItem(CACHE_KEY) || null;
};

/**
 * Clear photo cache (call on logout or photo change)
 */
export const clearPhotoCache = () => {
  localStorage.removeItem(CACHE_KEY);
  localStorage.removeItem(CACHE_TIMESTAMP);
};

/**
 * Update cache with new photo URL and timestamp
 * @param {string} photoUrl - New profile photo URL
 * @param {string} timestamp - Photo update timestamp
 */
export const updatePhotoCache = (photoUrl, timestamp) => {
  if (photoUrl) {
    localStorage.setItem(CACHE_KEY, photoUrl);
    localStorage.setItem(
      CACHE_TIMESTAMP,
      timestamp || new Date().toISOString()
    );
  } else {
    clearPhotoCache();
  }
};

/**
 * Check if cache is valid for a given timestamp
 * @param {string} currentTimestamp - Current photo timestamp from server
 * @returns {boolean} True if cache is still valid
 */
export const isCacheValid = (currentTimestamp) => {
  const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP);
  return cachedTimestamp === currentTimestamp;
};
